import { useState } from "react";
import { adminApi } from "../../../lib/adminApi";
import { money } from "../../../lib/api";
import { useConfig } from "../../../context/BrandContext";
import { Card, KV, TextInput, TextArea, SelectInput, CheckInput, Btn, run } from "../AdminUI";

const LOC = [{ value: "residence", label: "Residence" }, { value: "mechanic", label: "Mechanic / shop" }, { value: "business", label: "Business" }, { value: "other", label: "Other" }];
const STOP_FIELDS = [["address", "Address"], ["zip", "ZIP"], ["contact_name", "Contact name"], ["contact_phone", "Contact phone"]];
const CUST_FIELDS = [["full_name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"]];

const strip = ({ city, state, ...rest }) => rest;
const blank = (v) => (v === "" || v === undefined ? null : v);

const toForm = (sh) => ({
  customer: { ...sh.customer, company: sh.customer.company || "" },
  item: { ...sh.item },
  pickup: strip(sh.pickup), delivery: strip(sh.delivery),
  pickup_date: sh.pickup_date, notes: sh.notes || "",
});

const toPayload = (f) => {
  const freight = f.item.item_type === "freight";
  const it = f.item;
  return {
    customer: { ...f.customer, company: blank(f.customer.company) },
    item: {
      ...it, year: freight ? null : Number(it.year) || null, make: freight ? null : blank(it.make), model: freight ? null : blank(it.model),
      vin: freight ? null : blank(it.vin), description: freight ? blank(it.description) : null,
      weight_lbs: freight && it.weight_lbs ? Number(it.weight_lbs) : null, dimensions: freight ? blank(it.dimensions) : null,
    },
    pickup: f.pickup, delivery: f.delivery, pickup_date: f.pickup_date, notes: blank(f.notes),
  };
};

const PriceCard = ({ sh, apply }) => {
  const [price, setPrice] = useState(sh.final_price ?? "");
  const save = async () => {
    const r = await run(() => adminApi.patch(`/admin/shipments/${sh.tracking_number}`, { final_price: price === "" ? null : Number(price) }), "Final price saved");
    if (r) apply(r.data);
  };
  const q = sh.quote;
  return (
    <Card title="Pricing" testid="pricing-card">
      <div className="grid sm:grid-cols-3 gap-5 items-end">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Online estimate</div>
          <div className="mt-1 font-display font-black text-3xl text-slate-900" data-testid="pricing-estimate">{money(q.estimate)}</div>
          <div className="text-xs text-slate-500">{q.long_distance ? "Long distance (negotiable) · " : ""}{q.miles.toLocaleString()} mi · {q.transit_days_min}–{q.transit_days_max} days</div>
        </div>
        <TextInput label="Final price (USD)" type="number" min="0" step="1" value={price} onChange={setPrice} testid="final-price-input" />
        <Btn onClick={save} data-testid="final-price-save">Save final price</Btn>
      </div>
    </Card>
  );
};

const View = ({ sh }) => {
  const it = sh.item;
  const freight = it.item_type === "freight";
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Customer"><dl>{CUST_FIELDS.map(([k, l]) => <KV key={k} k={l} v={sh.customer[k]} testid={`view-customer-${k}`} />)}</dl></Card>
      <Card title="Vehicle / item"><dl>
        <KV k="Type" v={it.item_type} />
        {freight ? (<><KV k="Description" v={it.description} /><KV k="Weight (lbs)" v={it.weight_lbs} /><KV k="Dimensions" v={it.dimensions} /></>)
          : (<><KV k="Vehicle" v={`${it.year} ${it.make} ${it.model}`} /><KV k="VIN" v={it.vin} testid="view-vin" /><KV k="Operable" v={it.operable ? "Yes" : "No"} /></>)}
        <KV k="Carrier type" v={it.transport_type} /><KV k="Expedited" v={it.expedited ? "Yes" : "No"} />
      </dl></Card>
      {["pickup", "delivery"].map((k) => (
        <Card key={k} title={k === "pickup" ? "Pickup" : "Delivery"}><dl>
          <KV k="Address" v={`${sh[k].address}, ${sh[k].city}, ${sh[k].state} ${sh[k].zip}`} testid={`view-${k}-address`} />
          <KV k="Contact" v={sh[k].contact_name} /><KV k="Phone" v={sh[k].contact_phone} />
          {k === "delivery" && <KV k="Location type" v={LOC.find((l) => l.value === sh.delivery.location_type)?.label} />}
          {k === "pickup" && <KV k="Preferred date" v={sh.pickup_date} />}
        </dl></Card>
      ))}
      <Card title="Customer notes" className="lg:col-span-2"><p className="text-sm text-slate-700 whitespace-pre-wrap">{sh.notes || "—"}</p></Card>
    </div>
  );
};

const Edit = ({ sh, apply, done }) => {
  const { item_types } = useConfig();
  const [f, setF] = useState(() => toForm(sh));
  const set = (sec, k) => (v) => setF((p) => (sec ? { ...p, [sec]: { ...p[sec], [k]: v } } : { ...p, [k]: v }));
  const it = f.item;
  const save = async () => {
    const r = await run(() => adminApi.patch(`/admin/shipments/${sh.tracking_number}`, toPayload(f)), "Shipment updated");
    if (r) { apply(r.data); done(); }
  };
  return (
    <div className="space-y-6" data-testid="details-edit-form">
      <Card title="Customer"><div className="grid sm:grid-cols-2 gap-4">{CUST_FIELDS.map(([k, l]) => <TextInput key={k} label={l} value={f.customer[k]} onChange={set("customer", k)} testid={`edit-customer-${k}`} />)}</div></Card>
      <Card title="Vehicle / item"><div className="grid sm:grid-cols-3 gap-4">
        <SelectInput label="Type" value={it.item_type} onChange={set("item", "item_type")} options={item_types} testid="edit-item-type" />
        <SelectInput label="Carrier type" value={it.transport_type} onChange={set("item", "transport_type")} options={[{ value: "open", label: "Open" }, { value: "enclosed", label: "Enclosed" }]} testid="edit-item-transport" />
        <div className="flex flex-col justify-end gap-2 pb-1">
          <CheckInput label="Operable" checked={it.operable} onChange={set("item", "operable")} testid="edit-item-operable" />
          <CheckInput label="Expedited" checked={it.expedited} onChange={set("item", "expedited")} testid="edit-item-expedited" />
        </div>
        {it.item_type === "freight" ? (<>
          <TextArea label="Description" value={it.description} onChange={set("item", "description")} testid="edit-item-description" className="sm:col-span-3" />
          <TextInput label="Weight (lbs)" type="number" value={it.weight_lbs} onChange={set("item", "weight_lbs")} testid="edit-item-weight" />
          <TextInput label="Dimensions" value={it.dimensions} onChange={set("item", "dimensions")} testid="edit-item-dimensions" />
        </>) : (<>
          <TextInput label="Year" type="number" value={it.year} onChange={set("item", "year")} testid="edit-item-year" />
          <TextInput label="Make" value={it.make} onChange={set("item", "make")} testid="edit-item-make" />
          <TextInput label="Model" value={it.model} onChange={set("item", "model")} testid="edit-item-model" />
          <TextInput label="VIN" value={it.vin} onChange={(v) => set("item", "vin")(v.toUpperCase().slice(0, 17))} testid="edit-item-vin" />
        </>)}
      </div></Card>
      {["pickup", "delivery"].map((k) => (
        <Card key={k} title={k === "pickup" ? "Pickup" : "Delivery"}><div className="grid sm:grid-cols-2 gap-4">
          {STOP_FIELDS.map(([fk, l]) => <TextInput key={fk} label={l} value={f[k][fk]} onChange={set(k, fk)} testid={`edit-${k}-${fk}`} />)}
          {k === "delivery" && <SelectInput label="Location type" value={f.delivery.location_type} onChange={set("delivery", "location_type")} options={LOC} testid="edit-delivery-location-type" />}
          {k === "pickup" && <TextInput label="Preferred pickup date" type="date" value={f.pickup_date} onChange={set(null, "pickup_date")} testid="edit-pickup-date" />}
        </div></Card>
      ))}
      <Card title="Customer notes"><TextArea label="Notes" value={f.notes} onChange={set(null, "notes")} testid="edit-notes" /></Card>
      <div className="flex gap-3 justify-end">
        <Btn variant="ghost" onClick={done} data-testid="details-cancel-edit">Cancel</Btn>
        <Btn onClick={save} data-testid="details-save">Save changes</Btn>
      </div>
    </div>
  );
};

export const DetailsTab = ({ sh, apply }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="space-y-6" data-testid="details-tab">
      <PriceCard key={sh.final_price} sh={sh} apply={apply} />
      {!editing && <div className="flex justify-end"><Btn variant="ghost" onClick={() => setEditing(true)} data-testid="details-edit-button">Edit details</Btn></div>}
      {editing ? <Edit sh={sh} apply={apply} done={() => setEditing(false)} /> : <View sh={sh} />}
    </div>
  );
};
